import { API_CONFIG } from '../config';
import { adminAuthHeaders } from './adminAuth';

const BASE = API_CONFIG.BASE_URL;
const headers = { 'Content-Type': 'application/json' };
const authHeaders = () => ({ ...headers, ...adminAuthHeaders() });

const buildUrl = (experience) => {
    let url = `${BASE}/management/content`;
    if (experience) url += `?experience=${encodeURIComponent(experience)}`;
    return url;
};

/**
 * GET /management/content[?experience=arcade]
 *
 * El backend tiene un blob por experiencia. Sin parámetro lee `content.json`
 * (frontend principal / experiencia 'ambiental'). Con `experience='arcade'`
 * lee `content-arcade.json` (frontend lite de negociación).
 *
 * Respuesta: { updated_at?, es: { escenarios, scene_moments?, skill_config, ... }, en: { ... } }
 */
export async function fetchAdminContent({ experience } = {}) {
    const res = await fetch(buildUrl(experience), { headers });
    if (!res.ok) throw new Error(`fetchAdminContent: ${res.status}`);
    return res.json();
}

/**
 * POST /management/content[?experience=arcade]
 * El blob donde se guarda lo decide ?experience=. Sin parámetro guarda en
 * content.json (ambiental). Responde { ok, updated_at, experience }.
 */
export async function publishAdminContent(payload, { experience } = {}) {
    const res = await fetch(buildUrl(experience), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        let msg = `publishAdminContent: ${res.status}`;
        try {
            const body = await res.json();
            if (body.error) msg = body.error;
        } catch (_) {}
        throw new Error(msg);
    }
    return res.json();
}

/**
 * DELETE /management/content[?experience=arcade]
 * Idempotente: no falla si el blob no existía.
 */
export async function deleteAdminContent({ experience } = {}) {
    const res = await fetch(buildUrl(experience), {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`deleteAdminContent: ${res.status}`);
    return res.json();
}

/**
 * POST /management/login — valida la clave actual contra ADMIN_PASSWORD del backend.
 * Solo se usa para verificar antes de guardar la clave en sessionStorage.
 * Pasa la clave directamente (no usa la guardada). El prefijo /admin lo reserva
 * el host de Azure Functions, por eso usamos /management/.
 */
export async function loginAdmin(password) {
    const res = await fetch(`${BASE}/management/login`, {
        method: 'POST',
        headers: { ...headers, 'x-admin-password': password || '' },
    });
    if (res.status === 401) throw new Error('Clave incorrecta');
    if (res.status === 503) throw new Error('Backend sin ADMIN_PASSWORD configurado');
    if (!res.ok) throw new Error(`loginAdmin: ${res.status}`);
    return res.json();
}

/**
 * GET /management/sessions — lista sesiones del checkpointer LangGraph.
 * Filtros opcionales: { limit, threadIdContains }.
 * Respuesta: { ok, count, items: [{ thread_id, summary, survey? }] }
 */
export async function listAdminSessions(filters = {}) {
    const params = new URLSearchParams();
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.threadIdContains) params.set('thread_id_contains', filters.threadIdContains);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    const qs = params.toString();
    const url = `${BASE}/management/sessions${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, { headers: authHeaders() });
    if (res.status === 401) throw new Error('No autorizado');
    if (!res.ok) throw new Error(`listAdminSessions: ${res.status}`);
    return res.json();
}

/**
 * GET /management/session?thread_id=... — detalle completo de una sesion.
 */
export async function getAdminSession(threadId) {
    const url = `${BASE}/management/session?thread_id=${encodeURIComponent(threadId)}`;
    const res = await fetch(url, { headers: authHeaders() });
    if (res.status === 401) throw new Error('No autorizado');
    if (!res.ok) throw new Error(`getAdminSession: ${res.status}`);
    return res.json();
}

/**
 * GET /management/surveys — lista encuestas guardadas en blob storage.
 * Filtros opcionales: { clientId, threadId, from, to, limit }.
 * Respuesta: { ok, count, items: [...] }
 */
export async function listAdminSurveys(filters = {}) {
    const params = new URLSearchParams();
    if (filters.clientId) params.set('client_id', filters.clientId);
    if (filters.threadId) params.set('thread_id', filters.threadId);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (filters.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    const url = `${BASE}/management/surveys${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, { headers: authHeaders() });
    if (res.status === 401) throw new Error('No autorizado');
    if (!res.ok) throw new Error(`listAdminSurveys: ${res.status}`);
    return res.json();
}
